import { FormControl, Paper, Stack, Typography } from "@mui/material"
import React, { useEffect, useMemo, useState } from "react"
import StepPaymentItem from "./StepPaymentItem/StepPaymentItem"
import { checkoutPayment6Style } from "./PaymentConsts"
import { TPaymentMethodEnum } from "../../../../../core/types/Product"
import PriceInput from "../../../../Inputs/PriceInput"
import { UseFormSetValue, UseFormWatch } from "react-hook-form"
import { TSale } from "../../../../../core/types/Sale"
import NumberFunctions from "../../../../../core/functions/NumberFunctions"
import { useCheckoutContext } from "../../../../../core/hooks/useCheckoutContext"

interface PixPaymentPriceProps {
  visible: boolean
  title?: string
  index: number
  isMultiple?: boolean
  watch: UseFormWatch<TSale>
  setValue: UseFormSetValue<TSale>
}

const PixPaymentPrice = ({
  visible,
  title,
  index,
  isMultiple = false,
  watch,
  setValue,
}: PixPaymentPriceProps) => {
  const { displayCurrency } = useCheckoutContext()
  const [errorValue, setErrorValue] = useState("")

  const values = useMemo(
    () => ({
      paymentPixValue: watch(`payment.payments.${index}.value`),
      paymentTotal: watch("payment.total"),
    }),
    [watch(`payment.payments.${index}.value`), watch("payment.total")]
  )

  useEffect(() => {
    if ((values.paymentPixValue ?? 5) < 5) {
      setErrorValue(
        `O valor precisa ser maior que ${NumberFunctions.formatMoneyCurrency(
          5, displayCurrency
        )}`
      )
    }
  }, [values.paymentPixValue])

  return (
    <Stack spacing={1}>
      <Paper
        elevation={0}
        sx={{ borderRadius: "8px", backgroundColor: "#F2F2FF" }}
      >
        {title && isMultiple && (
          <Stack direction="column" spacing={2} sx={{ p: "20px" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {checkoutPayment6Style[TPaymentMethodEnum.Pix].icon[0]}
              <Typography
                variant="body1"
                fontSize="14px"
                fontWeight={600}
                lineHeight="18px"
              >
                {title}
              </Typography>
            </Stack>

            {isMultiple && (
              <FormControl fullWidth>
                <PriceInput
                  label="Valor a pagar no cartão"
                  value={!values.paymentPixValue ? 0 : values.paymentPixValue}
                  error={errorValue !== ""}
                  InputProps={{
                    inputProps: { min: 5 },
                    sx: {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: errorValue !== "" ? "red" : "#BCC1FF",
                        borderWidth: "2px",
                      },
                      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: errorValue !== "" ? "red" : "#BCC1FF",
                        borderWidth: "2px",
                      },
                    },
                  }}
                  onChange={(value) => {
                    setValue(`payment.payments.${index}.value`, value)
                    setErrorValue("")
                  }}
                  disabled={index > 0}
                />
                <Typography
                  lineHeight="14px"
                  fontSize="12px"
                  variant="body2"
                  sx={{
                    color: "#5D5E61",
                    pt: 0.5,
                  }}
                >
                  O valor mínimo é {NumberFunctions.formatMoneyCurrency(5, displayCurrency)}
                </Typography>
                {errorValue !== "" && (
                  <Typography variant="overline" color={"red"}>
                    {errorValue}
                  </Typography>
                )}
              </FormControl>
            )}
          </Stack>
        )}
      </Paper>
      <Stack
        direction="column"
        spacing={1}
        sx={{ display: visible ? undefined : "none" }}
      >
        <Typography
          color="#464A5A"
          fontSize="16px"
          lineHeight="16px"
          fontWeight={500}
        >
          Clique em ”Finalizar Compra” para gerar o PIX. Informações sobre o
          pagamento via PIX:
        </Typography>

        <Typography
          color="#464A5A"
          fontSize="16px"
          lineHeight="16px"
          fontWeight={500}
        >
          <ul>
            <li>
              Valor a vista:{" "}
              <b style={{ fontSize: "20px" }}>
                {NumberFunctions.formatMoneyCurrency(values.paymentTotal, displayCurrency)}
              </b>
            </li>
            <li>
              Não pode ser parcelado! Use cartão de crédito para parcelar sua
              compra;
            </li>
            <li>Prazo de até 30 minutos para compensar.</li>
          </ul>
        </Typography>
      </Stack>
    </Stack>
  )
}

export default PixPaymentPrice
